import type { TFunction } from "i18next";
import { isNullish } from "remeda";
import type { FieldOption } from "src/general/forms/formTypes";
import type { BaseEntity, EntityId } from "src/services/entityServices";
import { isEmptyValue, multiSearch, type StringKeys } from "./commonUtils";

export const NO_VALUE = "isnull";
export const HAS_VALUE = "notnull";

export const SEARCH_SORT_PAGINATION_PARAMS = [
  "search",
  "order_by",
  "order_direction",
  "page",
  "page_size",
];

/** Maximum ID reserved for system-created entities. IDs 0–999 are system entities. */
export const SYSTEM_ENTITY_ID_MAX = 999;

function applyFiltersToEntities<T extends BaseEntity>(
  searchResultData: T[],
  filters: [StringKeys<T>, string][],
) {
  return searchResultData.filter((item) =>
    filters.every(([key, filterValue]) => {
      if (filterValue === NO_VALUE) {
        return isEmptyValue(item[key]);
      }
      const itemValue = item[key as keyof T];
      if (isNullish(itemValue)) {
        return false;
      }
      return filterValue.split(",").includes(`${itemValue}`);
    }),
  );
}

/** Apply client-side search and sorting to a list of entities */
export function processEntityList<T extends BaseEntity>({
  data,
  params,
  searchFields,
  sortKeys,
}: {
  data: T[];
  params: Record<string, string | undefined>;
  searchFields: string[];
  sortKeys: string[];
}): T[] {
  const searchResultData = !params.search
    ? data
    : multiSearch(data, searchFields, params.search);

  const filters = Object.entries(params).filter(
    ([key, value]) =>
      !SEARCH_SORT_PAGINATION_PARAMS.includes(key) && value !== undefined,
  ) as [StringKeys<T>, string][];

  // Apply filters
  const filteredData =
    filters.length > 0
      ? applyFiltersToEntities<T>(searchResultData, filters)
      : searchResultData;

  // Return early if no order_by specified or invalid sort key
  if (!params.order_by || !sortKeys.includes(params.order_by)) {
    return filteredData;
  }

  // Sort data
  const sortedData = filteredData.sort((a, b) => {
    const orderBy = params.order_by ?? "id";
    const aValue = a[orderBy];
    const bValue = b[orderBy];

    if (typeof aValue === "boolean" && typeof bValue === "boolean") {
      return aValue === bValue ? 0 : aValue ? -1 : 1;
    }

    return String(aValue).localeCompare(String(bValue));
  });

  // Apply direction
  return params.order_direction === "DESC" ? sortedData.reverse() : sortedData;
}

/** General entity name extractor. Most entities have required `name` field
 * but for the rest we try the most fitting field (in decreasing order) */
export const extractEntityDisplayName = (data: BaseEntity): string =>
  (
    data.name ??
    data.full_name ??
    data.friendly_name ??
    data.technical_name ??
    data.hardware_id ??
    data.description ??
    data.id
  ).toString();

/** Convert array of entities to input options */
export const entitiesToOptions = <T extends BaseEntity>(
  entities: T[],
  valueKey: string = "id",
): FieldOption[] =>
  entities.map((item) => ({
    label: extractEntityDisplayName(item),
    key: item[valueKey] as EntityId,
  }));

/** Returns true if the entity ID is in the system-reserved range (0–999). */
export function isSystemEntity(entity: { id: EntityId }): boolean {
  const id =
    typeof entity.id === "string" ? Number.parseInt(entity.id, 10) : entity.id;
  if (Number.isNaN(id)) return false;
  return id >= 0 && id <= SYSTEM_ENTITY_ID_MAX;
}

/**
 * Translate backend entity/type identifiers into human-readable labels.
 * Supports both singular object types and the plural table name values
 * returned by the system-search endpoint.
 */
export function translateEntityType(type: string, t: TFunction): string {
  switch (type) {
    case "user":
    case "users":
      return t("common::User");
    case "group":
    case "groups":
      return t("common::Group");
    case "sensor":
    case "sensors":
      return t("common::Sensor");
    case "resource":
    case "resources":
      return t("common::Resource");
    case "location":
    case "locations":
      return t("common::Location");
    case "location_group":
    case "location_groups":
      return t("common::Location Group");
    case "reader":
    case "rfid_reader":
    case "rfid_readers":
      return t("common::Reader");
    case "antenna":
    case "antennas":
      return t("common::Antenna");
    case "attribute_definition":
    case "attribute_definitions":
      return t("common::Attribute Definition");
    case "attribute_value":
      return t("common::Attribute Value");
    case "sensor_group":
    case "sensor_groups":
      return t("common::Sensor Group");
    case "data_task":
    case "data_tasks":
      return t("common::Data Task");
    case "image":
    case "images":
      return t("common::Image");
    case "custom":
      return t("common::Custom");
    default:
      return type;
  }
}
